import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';
import { 
  ArrowLeft, Scale, TrendingUp, TrendingDown, DollarSign, 
  AlertTriangle, CheckCircle, RefreshCw, Award, Building2,
  BarChart3, PieChartIcon, Activity
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for regions
const REGION_COLORS = {
  PJM: '#3B82F6',
  ERCOT: '#EF4444',
  CAISO: '#F59E0B',
  NYISO: '#8B5CF6',
  ISONE: '#10B981',
  MISO: '#EC4899',
  SPP: '#06B6D4'
};

const PIE_COLORS = ['#09409C', '#146CBB', '#229644', '#FFA500', '#FF521C'];

export default function TerritoryComparison() {
  const navigate = useNavigate();
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);

  // Fetch territories
  const { data: territoriesData, isLoading: territoriesLoading } = useQuery({
    queryKey: ['territories'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/territories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Compare mutation
  const compareMutation = useMutation({
    mutationFn: async (regions) => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/territories/compare`,
        { regions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setComparisonData(data);
    },
  });

  const toggleRegion = (region) => {
    setSelectedRegions(prev => {
      if (prev.includes(region)) {
        return prev.filter(r => r !== region);
      }
      if (prev.length >= 4) {
        return prev;
      }
      return [...prev, region];
    });
  };

  const runComparison = () => {
    if (selectedRegions.length >= 2) {
      compareMutation.mutate(selectedRegions);
    }
  };

  const territories = territoriesData?.territories || [];

  // Prepare radar chart data
  const radarData = comparisonData?.comparison?.map(region => ({
    region: region.region,
    competitiveness: region.stats.avgCompetitiveness,
    renewalRate: region.stats.renewalRate,
    healthyPercent: region.stats.healthyPercent,
    avgValueScore: Math.min(100, (region.stats.avgValue / 10000)), // Normalize to 0-100
    riskScore: 100 - region.stats.atRiskPercent
  })) || [];

  // Prepare comparison bar data
  const valueComparisonData = comparisonData?.comparison?.map(region => ({
    region: region.region,
    totalValue: region.stats.totalValue,
    atRiskValue: region.stats.atRiskValue || 0
  })) || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="territory-comparison">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/map/territory')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Territory Map
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-gradient-primary">Territory Comparison</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                Compare ISO regions side-by-side with detailed analytics
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/map/trends')}
          >
            <Activity className="w-4 h-4 mr-1" />
            Trend Analysis
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Region Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#09409C]" />
              Select Regions to Compare
            </CardTitle>
            <CardDescription>
              Choose 2-4 ISO regions for side-by-side comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-3 mb-4">
              {territories.map((territory) => (
                <div
                  key={territory.iso_region}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedRegions.includes(territory.iso_region)
                      ? 'border-[#09409C] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${selectedRegions.length >= 4 && !selectedRegions.includes(territory.iso_region) ? 'opacity-50' : ''}`}
                  onClick={() => toggleRegion(territory.iso_region)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox 
                      checked={selectedRegions.includes(territory.iso_region)}
                      onCheckedChange={() => toggleRegion(territory.iso_region)}
                    />
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: REGION_COLORS[territory.iso_region] }}
                    />
                    <span className="font-semibold text-sm">{territory.iso_region}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {territory.stats?.contractCount || 0} contracts
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{selectedRegions.length}/4 selected</Badge>
                {selectedRegions.length < 2 && (
                  <span className="text-xs text-muted-foreground">Select at least 2 regions</span>
                )}
              </div>
              <Button 
                onClick={runComparison}
                disabled={selectedRegions.length < 2 || compareMutation.isPending}
                className="bg-[#09409C] hover:bg-[#146CBB]"
              >
                {compareMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Compare Regions
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        {comparisonData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground mb-1">Regions Compared</div>
                  <div className="text-2xl font-bold">{comparisonData.summary.regionsCompared}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground mb-1">Total Contracts</div>
                  <div className="text-2xl font-bold">{comparisonData.summary.totalContracts}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground mb-1">Combined Value</div>
                  <div className="text-2xl font-bold text-[#229644]">
                    {formatCurrency(comparisonData.summary.totalValue)}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground mb-1">Avg Competitiveness</div>
                  <div className="text-2xl font-bold">{comparisonData.summary.avgCompetitiveness}%</div>
                </CardContent>
              </Card>
            </div>

            {/* Rankings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Regional Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-600 font-medium mb-2">Highest Value</div>
                    {comparisonData.rankings.byValue.map((region, idx) => (
                      <div key={region} className="flex items-center gap-2 text-sm">
                        <span className={idx === 0 ? 'font-bold text-blue-800' : 'text-gray-600'}>
                          {idx + 1}. {region}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-green-600 font-medium mb-2">Best Competitiveness</div>
                    {comparisonData.rankings.byCompetitiveness.map((region, idx) => (
                      <div key={region} className="flex items-center gap-2 text-sm">
                        <span className={idx === 0 ? 'font-bold text-green-800' : 'text-gray-600'}>
                          {idx + 1}. {region}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="text-xs text-purple-600 font-medium mb-2">Best Renewal Rate</div>
                    {comparisonData.rankings.byRenewalRate.map((region, idx) => (
                      <div key={region} className="flex items-center gap-2 text-sm">
                        <span className={idx === 0 ? 'font-bold text-purple-800' : 'text-gray-600'}>
                          {idx + 1}. {region}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="text-xs text-orange-600 font-medium mb-2">Lowest Risk</div>
                    {comparisonData.rankings.lowestRisk.map((region, idx) => (
                      <div key={region} className="flex items-center gap-2 text-sm">
                        <span className={idx === 0 ? 'font-bold text-orange-800' : 'text-gray-600'}>
                          {idx + 1}. {region}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Side-by-Side Comparison Cards */}
            <div className={`grid grid-cols-${Math.min(comparisonData.comparison.length, 4)} gap-4`}>
              {comparisonData.comparison.map((region) => (
                <Card key={region.region} className="border-2" style={{ borderColor: REGION_COLORS[region.region] }}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: REGION_COLORS[region.region] }}
                      />
                      <CardTitle className="text-lg">{region.region}</CardTitle>
                    </div>
                    <CardDescription className="text-xs">{region.territoryName}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <div className="text-xs text-muted-foreground">Contracts</div>
                        <div className="text-xl font-bold">{region.stats.contractCount}</div>
                      </div>
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <div className="text-xs text-muted-foreground">Total Value</div>
                        <div className="text-sm font-bold text-[#229644]">
                          {formatCurrency(region.stats.totalValue)}
                        </div>
                      </div>
                    </div>

                    {/* Health Metrics */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Healthy
                        </span>
                        <Badge className="bg-green-100 text-green-800">
                          {region.stats.healthyPercent}%
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                          At Risk
                        </span>
                        <Badge className="bg-red-100 text-red-800">
                          {region.stats.atRiskPercent}%
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3 text-blue-500" />
                          Renewal Rate
                        </span>
                        <Badge className="bg-blue-100 text-blue-800">
                          {region.stats.renewalRate}%
                        </Badge>
                      </div>
                    </div>

                    {/* Avg Competitiveness Gauge */}
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Avg Competitiveness</span>
                        <span className="text-sm font-bold">{region.stats.avgCompetitiveness}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            region.stats.avgCompetitiveness >= 70 ? 'bg-green-500' :
                            region.stats.avgCompetitiveness >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${region.stats.avgCompetitiveness}%` }}
                        />
                      </div>
                    </div>

                    {/* Top Accounts */}
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-2">Top Accounts</div>
                      <div className="space-y-1">
                        {region.topAccounts.slice(0, 3).map((acc, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="truncate max-w-[60%]">{acc.accountName}</span>
                            <span className="font-medium">{formatCurrency(acc.totalValue)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-2 gap-6">
              {/* Value Comparison Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#229644]" />
                    Portfolio Value Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={valueComparisonData}>
                      <XAxis dataKey="region" />
                      <YAxis tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="totalValue" name="Total Value" fill="#09409C" />
                      <Bar dataKey="atRiskValue" name="At-Risk Value" fill="#FF521C" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Expiration Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#09409C]" />
                    Expiration Timeline (6 Months)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart>
                      <XAxis 
                        dataKey="month" 
                        type="category"
                        allowDuplicatedCategory={false}
                      />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      {comparisonData.comparison.map((region) => (
                        <Line
                          key={region.region}
                          data={region.expirationTimeline}
                          dataKey="value"
                          name={region.region}
                          stroke={REGION_COLORS[region.region]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Rate Type Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-[#09409C]" />
                    Rate Type Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {comparisonData.comparison.slice(0, 2).map((region) => (
                      <div key={region.region} className="text-center">
                        <div className="text-sm font-medium mb-2">{region.region}</div>
                        <ResponsiveContainer width="100%" height={150}>
                          <PieChart>
                            <Pie
                              data={region.rateDistribution}
                              dataKey="count"
                              nameKey="type"
                              cx="50%"
                              cy="50%"
                              innerRadius={30}
                              outerRadius={50}
                            >
                              {region.rateDistribution.map((entry, idx) => (
                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap justify-center gap-2 mt-2">
                          {region.rateDistribution.map((item, idx) => (
                            <Badge 
                              key={item.type} 
                              variant="outline" 
                              className="text-[10px]"
                              style={{ borderColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                            >
                              {item.type}: {item.percent}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Queue Status Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#09409C]" />
                    Pipeline Status by Region
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart 
                      data={comparisonData.comparison.map(r => ({
                        region: r.region,
                        Pending: r.queueStats.pending,
                        'In Progress': r.queueStats.inProgress,
                        Renewed: r.queueStats.renewed,
                        Lost: r.queueStats.lost
                      }))}
                    >
                      <XAxis dataKey="region" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Pending" stackId="a" fill="#9CA3AF" />
                      <Bar dataKey="In Progress" stackId="a" fill="#3B82F6" />
                      <Bar dataKey="Renewed" stackId="a" fill="#22C55E" />
                      <Bar dataKey="Lost" stackId="a" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!comparisonData && !compareMutation.isPending && (
          <Card className="py-12">
            <CardContent className="text-center">
              <Scale className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold mb-2">Select Regions to Compare</h3>
              <p className="text-muted-foreground">
                Choose 2-4 ISO regions above to see detailed side-by-side analytics
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
